import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Printer, Ruler, Trash2 } from 'lucide-react'
import { deleteMeasurement, listJobMeasurements, listMeasurements, removeJobMeasurement, saveJobMeasurement, saveMeasurement } from '../../api/jobs'
import { getCustomers } from '../../api/master'
import Button from '../../components/Button'
import HelpTip from '../../components/HelpTip'
import Modal from '../../components/Modal'
import { Card } from '../../components/Summary'
import { useToast } from '../../components/Toast.jsx'
import { fmtDay, localToday } from '../../utils/format'
import { Field, customerName } from '../master/masterShared'

// Customer measurements for made-to-order work such as tailoring: named
// sets ("Wedding 2026"), each a list of measurements in inches or cm. A job
// is made to any number of sets, each for a garment or a person, from any
// customer. Part of Jobs.

// A starting list for a garment; every row can be renamed or removed.
const STARTER = ['Chest / bust', 'Waist', 'Hip', 'Shoulder', 'Sleeve length', 'Round sleeve', 'Neck',
  'Top length', 'Trouser / skirt length', 'Inseam', 'Thigh', 'Knee', 'Ankle']
const UNIT = { in: 'in', cm: 'cm' }

const blank = (customerId) => ({ customer_id: customerId, name: '', unit: 'in', taken_on: localToday(), notes: '',
  items: STARTER.map((label) => ({ label, value: '' })) })

/** The values of one set, as a grid of small tiles. */
export function MeasurementGrid({ set }) {
  const shown = (set.items || []).filter((i) => i.value !== '')
  if (!shown.length) return <p className="sum-muted">No values entered yet.</p>
  return (
    <div className="msr-grid">
      {shown.map((i, n) => (
        <div key={n} className="msr-item"><span>{i.label}</span><strong>{i.value}{/^[\d.,\s/]+$/.test(i.value) ? ` ${UNIT[set.unit] || ''}` : ''}</strong></div>
      ))}
    </div>
  )
}

/** Add or edit a set. `value` is the set (or a blank one); `onSaved(set)`. */
export function MeasurementEditor({ value, onClose, onSaved }) {
  const { notify } = useToast()
  const [form, setForm] = useState(value)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  useEffect(() => { setForm(value); setErrors({}) }, [value])
  if (!form) return null
  const set = (patch) => setForm((f) => ({ ...f, ...patch }))
  const setItem = (n, patch) => set({ items: form.items.map((it, i) => (i === n ? { ...it, ...patch } : it)) })

  const save = async () => {
    setSaving(true)
    setErrors({})
    try {
      const saved = await saveMeasurement(form)
      notify(form.measurement_id ? 'Measurements saved' : 'Measurements added')
      onSaved(saved)
    } catch (e) {
      setErrors(e.fields || { name: e.message || 'Couldn’t save the measurements.' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      size="lg"
      onClose={onClose}
      title={form.measurement_id ? 'Edit measurements' : 'New measurements'}
      subtitle="Fill in what you measured; empty rows aren’t kept. Rename or add rows for any garment."
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving}><Ruler size={14} />Save measurements</Button>
      </>}
    >
      <div className="ci-form-meta-top two">
        <Field label="Name" error={errors.name} hint="Such as “Wedding 2026” or “Kaftan”.">
          <input value={form.name} onChange={(e) => set({ name: e.target.value })} autoFocus />
        </Field>
        <Field label="Taken on" error={errors.taken_on}>
          <input type="date" value={form.taken_on || ''} onChange={(e) => set({ taken_on: e.target.value })} />
        </Field>
      </div>
      <div className="segmented msr-unit" role="radiogroup" aria-label="Unit">
        {Object.entries({ in: 'Inches', cm: 'Centimetres' }).map(([k, label]) => (
          <button key={k} type="button" role="radio" aria-checked={form.unit === k} className={form.unit === k ? 'active' : ''} onClick={() => set({ unit: k })}>{label}</button>
        ))}
      </div>
      {errors.items && <p className="acc-field-error">{errors.items}</p>}
      <table className="ci-table msr-table">
        <thead><tr><th>Measurement</th><th className="num">Value ({UNIT[form.unit]})</th><th aria-label="Remove" /></tr></thead>
        <tbody>
          {form.items.map((it, n) => (
            <tr key={n}>
              <td><input aria-label={`Measurement ${n + 1}`} value={it.label} onChange={(e) => setItem(n, { label: e.target.value })} />
                {errors[`items[${n}].label`] && <span className="acc-field-error">{errors[`items[${n}].label`]}</span>}</td>
              <td className="num"><input aria-label={`${it.label || 'Measurement'} value`} inputMode="decimal" value={it.value} onChange={(e) => setItem(n, { value: e.target.value })} placeholder="—" /></td>
              <td><button type="button" className="ci-form-del-btn" aria-label={`Remove ${it.label || 'row'}`} onClick={() => set({ items: form.items.filter((_, i) => i !== n) })}><Trash2 size={15} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => set({ items: [...form.items, { label: '', value: '' }] })}><Plus size={14} />Add a measurement</button>
      <Field label="Notes (optional)">
        <textarea className="ci-form-memo-textarea" value={form.notes || ''} onChange={(e) => set({ notes: e.target.value })} placeholder="Posture, fit preferences, fabric notes…" />
      </Field>
    </Modal>
  )
}

/** A customer's sets, on their page: add, edit and delete. */
export function CustomerMeasurements({ customerId }) {
  const { notify } = useToast()
  const [sets, setSets] = useState(null)
  const [editing, setEditing] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [error, setError] = useState('')
  const load = () => listMeasurements(customerId).then(setSets).catch((e) => { setSets([]); setError(e.message || 'Couldn’t load the measurements.') })
  useEffect(() => { load() }, [customerId]) // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async () => {
    try {
      await deleteMeasurement(deleting.measurement_id)
      notify('Measurements deleted')
      setDeleting(null)
      load()
    } catch (e) {
      setError(e.message || 'Couldn’t delete them.')
      setDeleting(null)
    }
  }

  const editor = editing && <MeasurementEditor value={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load() }} />
  // No sets yet: one quiet line instead of a whole card.
  if (sets && !sets.length && !error) {
    return (
      <div className="msr-empty">
        <Ruler size={14} aria-hidden="true" />
        <span>No measurements yet.</span>
        <button type="button" className="msr-link" onClick={() => setEditing(blank(customerId))}>Add measurements</button>
        {editor}
      </div>
    )
  }

  return (
    <Card
      title={<>Measurements <HelpTip text="For made-to-order work: keep each customer’s measurements here and choose them on a job, so every order shows what it was cut to." /></>}
      action={<button type="button" className="sum-card-link" onClick={() => setEditing(blank(customerId))}>Add measurements</button>}
    >
      {error && <p className="sum-warn">{error}</p>}
      {sets == null ? <p className="sum-muted">Loading…</p> : (
        <div className="msr-sets">
          {sets.map((s) => (
            <div key={s.measurement_id} className="msr-set">
              <div className="msr-set-head">
                <strong>{s.name}</strong>
                <span className="cell-sub">{[s.taken_on && `Taken ${fmtDay(s.taken_on)}`, s.unit === 'cm' ? 'Centimetres' : 'Inches'].filter(Boolean).join(' · ')}</span>
                <span className="msr-set-actions">
                  <button type="button" className="msr-link" onClick={() => setEditing(s)}>Edit</button>
                  <button type="button" className="msr-link" onClick={() => setDeleting(s)}>Delete</button>
                </span>
              </div>
              <MeasurementGrid set={s} />
              {s.notes && <p className="cell-sub">{s.notes}</p>}
            </div>
          ))}
        </div>
      )}
      {editor}
      <Modal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title="Delete these measurements?"
        subtitle={deleting ? `“${deleting.name}” will be removed, and taken off any jobs that used it.` : ''}
        footer={<>
          <Button variant="ghost" onClick={() => setDeleting(null)}>Keep them</Button>
          <Button variant="danger" onClick={remove}>Delete</Button>
        </>}
      />
    </Card>
  )
}

/** The job's measurements: the sets it's made to, each for a garment or a
 *  person (one row per bridesmaid, say), from any customer: the job's own
 *  by default. Tap a row to see its numbers. */
export function JobMeasurements({ job }) {
  const { notify } = useToast()
  const [links, setLinks] = useState(job.measurements || [])
  const [adding, setAdding] = useState(null) // {} to add one, or a row to change
  const [editing, setEditing] = useState(null) // a set's numbers to edit
  const [removing, setRemoving] = useState(null)
  const [shown, setShown] = useState({})
  const [error, setError] = useState('')
  useEffect(() => { setLinks(job.measurements || []) }, [job.measurements])

  const isOpen = (l) => shown[l.link_id] ?? links.length === 1
  const pieces = links.reduce((n, l) => n + (Number(l.pieces) || 0), 0)
  const remove = async () => {
    setError('')
    try {
      setLinks(await removeJobMeasurement(job.job_id, removing.link_id))
      notify('Measurements taken off the job')
    } catch (e) {
      setError(e.message || 'Couldn’t take them off.')
    } finally {
      setRemoving(null)
    }
  }

  return (
    <div id="measurements">
      <Card
        title={<>Measurements <HelpTip text="The measurements this order is made to: one row for each garment or each person (a wedding party, a family’s aso-ebi, a school’s uniforms). A row can use any customer’s set; the sets stay on their customers, so later orders can use them too." /></>}
        action={<button type="button" className="sum-card-link" onClick={() => setAdding({})}>Add measurements</button>}
      >
        {error && <p className="sum-warn">{error}</p>}
        {links.length === 0 ? (
          <p className="sum-muted"><Ruler size={14} aria-hidden="true" /> No measurements on this job yet.</p>
        ) : (
          <div className="msr-sets">
            {links.map((l) => {
              const other = l.set.customer_id && job.customer_id && Number(l.set.customer_id) !== Number(job.customer_id)
              return (
                <div key={l.link_id} className="msr-set">
                  <div className="msr-set-head">
                    <button type="button" className="msr-row-toggle" aria-expanded={isOpen(l)} onClick={() => setShown((v) => ({ ...v, [l.link_id]: !isOpen(l) }))}>
                      <strong>{l.label || l.set.name}</strong>
                      <span className="cell-sub">{[l.label && l.set.name, (other || !job.customer_id) && l.customer_name, `${l.pieces} ${Number(l.pieces) === 1 ? 'piece' : 'pieces'}`].filter(Boolean).join(' · ')}</span>
                    </button>
                    <span className="msr-set-actions">
                      <button type="button" className="msr-link" onClick={() => setAdding(l)}>Change</button>
                      <button type="button" className="msr-link" onClick={() => setEditing(l.set)}>Edit sizes</button>
                      <button type="button" className="msr-link" onClick={() => setRemoving(l)}>Remove</button>
                    </span>
                  </div>
                  {isOpen(l) && (
                    <>
                      <MeasurementGrid set={l.set} />
                      <p className="cell-sub">{[l.set.unit === 'cm' ? 'Centimetres' : 'Inches', l.set.taken_on && `taken ${fmtDay(l.set.taken_on)}`, l.set.notes].filter(Boolean).join(' · ')}</p>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )}
        {links.length > 0 && (
          <div className="msr-card-foot">
            <span className="cell-sub">{links.length} {links.length === 1 ? 'set' : 'sets'} · {pieces} {pieces === 1 ? 'piece' : 'pieces'} in all</span>
            <Link className="ci-btn ci-btn-ghost ci-btn-sm" to={`/jobs/${job.job_id}/cutting-list`}><Printer size={14} />Cutting list</Link>
          </div>
        )}
      </Card>

      {adding && (
        <AddToJob
          job={job}
          link={adding.link_id ? adding : null}
          onClose={() => setAdding(null)}
          onSaved={(rows, wasChange) => { setLinks(rows); setAdding(null); notify(wasChange ? 'Measurements changed' : 'Measurements added to the job') }}
        />
      )}
      {editing && (
        <MeasurementEditor value={editing} onClose={() => setEditing(null)} onSaved={async () => {
          setEditing(null)
          setLinks(await listJobMeasurements(job.job_id).catch(() => links))
        }} />
      )}
      <Modal
        open={!!removing}
        onClose={() => setRemoving(null)}
        title="Take these measurements off the job?"
        subtitle={removing ? `“${removing.label || removing.set.name}” comes off this job. The measurements stay on ${removing.customer_name || 'their customer'}.` : ''}
        footer={<>
          <Button variant="ghost" onClick={() => setRemoving(null)}>Keep them</Button>
          <Button variant="danger" onClick={remove}>Take off</Button>
        </>}
      />
    </div>
  )
}

/** Add (or change) one of the job's rows: whose measurements, which set (or
 *  a new one), what it's for and how many pieces. */
function AddToJob({ job, link, onClose, onSaved }) {
  const [customers, setCustomers] = useState(null)
  const [customerId, setCustomerId] = useState(String(link?.set.customer_id || job.customer_id || ''))
  const [sets, setSets] = useState(null)
  const [setId, setSetId] = useState(link ? String(link.measurement_id) : '')
  const [label, setLabel] = useState(link?.label || '')
  const [pieces, setPieces] = useState(String(link?.pieces || 1))
  const [creating, setCreating] = useState(null)
  const [errors, setErrors] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getCustomers().then((rows) => setCustomers(Array.isArray(rows) ? rows : (rows?.rows || []))).catch(() => setCustomers([]))
  }, [])
  useEffect(() => {
    if (!customerId) { setSets([]); return }
    setSets(null)
    listMeasurements(Number(customerId)).then((rows) => {
      setSets(rows)
      if (!link && rows.length === 1) setSetId(String(rows[0].measurement_id))
    }).catch(() => setSets([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId])

  const whose = (customers || []).find((c) => String(c.customer_id) === customerId)
  const save = async () => {
    setSaving(true)
    setError('')
    setErrors({})
    try {
      const rows = await saveJobMeasurement(job.job_id, { link_id: link?.link_id, measurement_id: Number(setId) || 0, label, pieces })
      onSaved(rows, !!link)
    } catch (e) {
      setErrors(e.fields || {})
      setError(e.fields ? 'Check the highlighted fields.' : (e.message || 'Couldn’t save.'))
    } finally {
      setSaving(false)
    }
  }

  // A new set for the chosen customer, then back here with it chosen.
  if (creating) {
    return (
      <MeasurementEditor value={creating} onClose={() => setCreating(null)} onSaved={async (saved) => {
        setCreating(null)
        setSets(await listMeasurements(Number(customerId)).catch(() => sets || []))
        setSetId(String(saved.measurement_id))
      }} />
    )
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={link ? 'Change measurements' : 'Add measurements'}
      subtitle="Whose measurements, which set, and what it’s for. For a wedding party, add a row for each person."
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving}><Ruler size={14} />{link ? 'Save' : 'Add to the job'}</Button>
      </>}
    >
      {error && <p className="acc-field-error">{error}</p>}
      <div className="ci-form-meta-top">
        <Field label="Whose measurements" hint={job.customer_id && customerId !== String(job.customer_id) ? 'Another customer’s measurements, for this job.' : ''}>
          <select value={customerId} onChange={(e) => { setCustomerId(e.target.value); setSetId('') }} disabled={customers == null}>
            <option value="">{customers == null ? 'Loading…' : 'Choose a customer…'}</option>
            {(customers || []).map((c) => <option key={c.customer_id} value={c.customer_id}>{customerName(c)}{String(c.customer_id) === String(job.customer_id) ? ' (this job’s customer)' : ''}</option>)}
          </select>
        </Field>
        <Field label="Which set" error={errors.measurement_id}>
          <select value={setId} onChange={(e) => setSetId(e.target.value)} disabled={!customerId || sets == null}>
            <option value="">{!customerId ? 'Choose a customer first' : sets == null ? 'Loading…' : sets.length ? 'Choose a set…' : 'No measurements yet'}</option>
            {(sets || []).map((s) => <option key={s.measurement_id} value={s.measurement_id}>{s.name}{s.taken_on ? ` · ${fmtDay(s.taken_on)}` : ''}</option>)}
          </select>
        </Field>
        {customerId && (
          <p className="cell-sub"><button type="button" className="msr-link" onClick={() => setCreating(blank(Number(customerId)))}>+ New measurements for {whose ? customerName(whose) : 'this customer'}</button></p>
        )}
        <Field label="For (optional)" hint="Such as “Groom – agbada”, “Bride – iro and buba” or “Bridesmaids”.">
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} placeholder="What these measurements are for" />
        </Field>
        <Field label="Pieces" error={errors.pieces} hint="How many to make to these measurements.">
          <input value={pieces} onChange={(e) => setPieces(e.target.value)} inputMode="numeric" />
        </Field>
      </div>
    </Modal>
  )
}
